import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { streamAssistantChat } from "@/lib/stream";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import AiMarkdown from "@/components/AiMarkdown";
import VoiceRecorder from "@/components/VoiceRecorder";
import { EditBanner, MessageTools } from "@/components/MessageTools";
import { AttachButton, PiecesChips, retirerPiece } from "@/components/Attachments";
import { Apprentissage, stripRetenir } from "@/components/ZoeLearn";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Sparkles, Loader2, Send, Plus, Trash2, MessageSquare, X,
  Users2, FileText, Mail, Search, FileCheck, Edit3, BookOpen, StopCircle,
} from "lucide-react";

const MODES = [
  { value: "chat", label: "Chat", icon: MessageSquare },
  { value: "draft_convention", label: "Convention", icon: FileCheck },
  { value: "draft_courrier", label: "Courrier", icon: Mail },
  { value: "search_ged", label: "Recherche GED", icon: Search },
  { value: "devis_builder", label: "Devis", icon: FileCheck },
];

const CATEGORY_META = {
  convention: { label: "Conventions", icon: FileCheck, color: "#C5A059" },
  courrier_rh: { label: "Courriers RH", icon: Mail, color: "#5B8DEF" },
  note_interne: { label: "Notes internes", icon: FileText, color: "#7B6FBF" },
  procedure: { label: "Procédures", icon: BookOpen, color: "#43A974" },
  autre: { label: "Autres", icon: FileText, color: "#64748B" },
};

const SHAREABLE_ROLES = [
  { value: "admin", label: "Admin" },
  { value: "gestionnaire_ge", label: "Gestionnaires" },
  { value: "finance", label: "Finance" },
];

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function Assistant() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("chat");
  const [loading, setLoading] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [renaming, setRenaming] = useState(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [sharingId, setSharingId] = useState(null);
  const [shareRoles, setShareRoles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [tplCategory, setTplCategory] = useState("all");
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const inputRef = useRef(null);
  const [editing, setEditing] = useState(null);

  const isManager = ["admin", "gestionnaire_ge", "finance"].includes(user?.role);

  const loadSessions = async () => {
    try { const { data } = await api.get("/assistant/sessions"); setSessions(data || []); }
    catch { toast.error("Impossible de charger les conversations"); }
  };
  const loadMessages = async (sid) => {
    if (!sid) { setMessages([]); return; }
    setLoading(true);
    try { const { data } = await api.get(`/assistant/sessions/${sid}/messages`); setMessages(data.messages || []); }
    catch { toast.error("Impossible de charger la conversation"); }
    finally { setLoading(false); }
  };
  const loadTemplates = async () => {
    try { const { data } = await api.get("/assistant/templates"); setTemplates(data || []); }
    catch { /* silent */ }
  };

  useEffect(() => { loadSessions(); loadTemplates(); }, []);
  const skipLoad = useRef(false);
  useEffect(() => {
    if (skipLoad.current) { skipLoad.current = false; return; }
    loadMessages(activeId);
  }, [activeId]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sendingMsg, streamingText]);

  const newChat = () => {
    setActiveId(null); setMessages([]); setInput(""); setAttachments([]); setMode("chat"); setStreamingText("");
  };

  const send = async (q, forcedMode, editOf, piecesMsg) => {
    if (!q.trim() || sendingMsg) return;
    const jointes = piecesMsg ?? attachments;
    const token = localStorage.getItem("oscopge_token");
    const payload = {
      session_id: activeId || undefined,
      message: q.trim(),
      pieces: jointes.map((a) => a.id),
      mode: forcedMode || mode,
      edit_of: editOf?.id,
    };
    const sentAttMeta = jointes.map((a) => ({ id: a.id, filename: a.filename, kind: a.kind }));
    let ids = null;
    let acc = "";
    setMessages((m) => [...(editOf ? m.slice(0, editOf.index) : m), { role: "user", content: q.trim(), attachments_meta: sentAttMeta }]);
    setEditing(null);
    setInput("");
    setAttachments([]);
    setSendingMsg(true);
    setStreamingText("");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamAssistantChat({
        token, baseUrl: BASE_URL, body: payload, signal: controller.signal,
        onSession: (sid) => { if (!activeId) { skipLoad.current = true; setActiveId(sid); } },
        onDelta: (t) => { acc += t; setStreamingText(acc); },
        onDone: (d) => { ids = d; },
        onError: (err) => { toast.error(err.message || "Erreur IA"); },
      });
    } catch (err) {
      if (err.name !== "AbortError") toast.error(err.message || "Erreur streaming");
    } finally {
      // Commit the streamed text as a final assistant message
      setMessages((m) => {
        const out = [...m];
        const u = out.length - 1;
        if (ids?.user_message_id && out[u]?.role === "user") out[u] = { ...out[u], id: ids.user_message_id };
        if (acc) out.push({ role: "assistant", content: acc, id: ids?.message_id });
        return out;
      });
      setStreamingText("");
      setSendingMsg(false);
      abortRef.current = null;
      loadSessions();
    }
  };

  const startEdit = (m, i) => {
    setEditing({ id: m.id, index: i });
    setInput(m.content);
    setAttachments((m.attachments_meta || []).filter((a) => a.id));
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  const cancelEdit = () => { setEditing(null); setInput(""); setAttachments([]); };
  const submit = () => send(input, undefined, editing || undefined);

  const cancelStream = () => {
    try { abortRef.current?.abort(); } catch {}
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Supprimer cette conversation et ses pièces jointes ?")) return;
    try { await api.delete(`/assistant/sessions/${sid}`); toast.success("Conversation supprimée"); if (activeId === sid) newChat(); loadSessions(); }
    catch { toast.error("Suppression impossible"); }
  };
  const saveRename = async (sid) => {
    try { await api.patch(`/assistant/sessions/${sid}`, { title: renameTitle }); setRenaming(null); loadSessions(); }
    catch { toast.error("Renommage impossible"); }
  };
  const saveShare = async () => {
    try { await api.patch(`/assistant/sessions/${sharingId}`, { shared_with_roles: shareRoles }); toast.success("Partage mis à jour"); setSharingId(null); setShareRoles([]); loadSessions(); }
    catch { toast.error("Partage impossible"); }
  };

  const applyTemplate = (tpl) => {
    setMode(tpl.mode || "chat");
    setInput(tpl.prompt_template);
    setShowTemplates(false);
    toast.success(`Modèle "${tpl.title}" chargé — remplissez les crochets puis envoyez`);
  };

  const filteredTemplates = tplCategory === "all" ? templates : templates.filter((t) => t.category === tplCategory);
  const grouped = templates.reduce((acc, t) => { (acc[t.category] = acc[t.category] || []).push(t); return acc; }, {});

  return (
    <div data-testid="assistant-page" className="h-[calc(100vh-6rem)] flex gap-4">
      {/* Sidebar sessions */}
      <aside className="w-72 shrink-0 bg-white border border-slate-200 rounded-xl flex flex-col overflow-hidden">
        <div className="p-3 border-b border-slate-200 space-y-2">
          <Button onClick={newChat} data-testid="ai-new-chat" className="w-full bg-[#C5A059] hover:bg-[#A88846] text-white">
            <Plus className="h-4 w-4 mr-1" /> Nouvelle conversation
          </Button>
          <Button onClick={() => setShowTemplates(true)} variant="outline" data-testid="ai-open-templates" className="w-full border-[#C5A059]/40 text-[#A88846] hover:bg-[#FDF9F1]">
            <BookOpen className="h-4 w-4 mr-1" /> Bibliothèque de modèles
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {sessions.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-6">Aucune conversation.</p>
          )}
          {sessions.map((s) => {
            const isShared = s.user_id !== user?.id;
            return (
              <div key={s.id} className={`group rounded-md border transition-colors ${activeId === s.id ? "bg-[#FDF9F1] border-[#C5A059]" : "border-transparent hover:bg-slate-50"}`}>
                {renaming === s.id ? (
                  <div className="p-2 flex gap-1">
                    <Input autoFocus value={renameTitle} onChange={(e) => setRenameTitle(e.target.value)} className="h-8 text-xs" />
                    <Button size="sm" className="h-8 px-2" onClick={() => saveRename(s.id)}>OK</Button>
                  </div>
                ) : (
                  <div className="flex items-start p-2 gap-1">
                    <button onClick={() => setActiveId(s.id)} data-testid={`ai-session-${s.id}`} className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-1">
                        <MessageSquare className="h-3 w-3 text-slate-500 shrink-0" />
                        <span className="text-xs font-medium text-slate-800 truncate">{s.title}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-slate-400">{new Date(s.updated_at).toLocaleDateString("fr-FR")}</span>
                        {isShared && <span className="text-[10px] bg-[#C5A059]/10 text-[#A88846] px-1 rounded">partagée</span>}
                        {(s.shared_with_roles || []).length > 0 && !isShared && (
                          <span className="text-[10px] bg-blue-50 text-blue-700 px-1 rounded">{(s.shared_with_roles || []).length} rôle(s)</span>
                        )}
                      </div>
                    </button>
                    {!isShared && (
                      <div className="opacity-0 group-hover:opacity-100 flex flex-col gap-0.5 transition-opacity">
                        <button onClick={() => { setRenaming(s.id); setRenameTitle(s.title); }} title="Renommer" className="text-slate-400 hover:text-slate-700"><Edit3 className="h-3 w-3" /></button>
                        {isManager && <button onClick={() => { setSharingId(s.id); setShareRoles(s.shared_with_roles || []); }} title="Partager" className="text-slate-400 hover:text-[#A88846]"><Users2 className="h-3 w-3" /></button>}
                        <button onClick={() => deleteSession(s.id)} title="Supprimer" data-testid={`ai-delete-${s.id}`} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>

      {/* Chat area */}
      <section className="flex-1 bg-white border border-slate-200 rounded-xl flex flex-col overflow-hidden min-w-0">
        <div className="px-6 pt-5">
          <PageHeader
            title="ZOE — votre assistante O'SCOP GE"
            subtitle="Experte ESS, L1253, conventions et procédures GE Outre-mer. Pour administrer par message : ZOE — Administration."
          />
        </div>

        <div className="px-6 pb-2 flex flex-wrap gap-2 border-b border-slate-100">
          {MODES.map((m) => {
            const Icon = m.icon; const active = mode === m.value;
            return (
              <button key={m.value} onClick={() => setMode(m.value)} data-testid={`ai-mode-${m.value}`} className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1 ${active ? "bg-[#C5A059] text-white border-[#C5A059]" : "bg-white text-slate-600 border-slate-200 hover:border-[#C5A059]"}`}>
                <Icon className="h-3 w-3" /> {m.label}
              </button>
            );
          })}
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#C5A059]" /></div>
          ) : messages.length === 0 && !streamingText ? (
            <EmptyState templates={templates} onPick={applyTemplate} />
          ) : (
            <>
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[88%] rounded-xl px-4 py-3 text-sm ${m.role === "user" ? "bg-slate-900 text-white" : "bg-[#FDF9F1] text-slate-900 border border-[#C5A059]/20"}`}>
                    {m.role === "user" ? (
                      <>
                        <div className="whitespace-pre-wrap">{m.content}</div>
                        {(m.attachments_meta || []).length > 0 && (
                          <div className="mt-2"><PiecesChips pieces={m.attachments_meta} dark testId={`ai-msg-${i}`} /></div>
                        )}
                      </>
                    ) : (
                      <>
                        <AiMarkdown>{stripRetenir(m.content).texte}</AiMarkdown>
                        <Apprentissage contexte="conseil" sessionId={activeId} messageId={m.id || `idx-${i}`} reponse={m.content}
                          question={messages.slice(0, i).reverse().find((x) => x.role === "user")?.content}
                          propositions={stripRetenir(m.content).propositions} testId={`ai-learn-${i}`} />
                      </>
                    )}
                    {m.role === "user" && (
                      <div className="[&_button]:text-slate-300 [&_button:hover]:text-white [&_button:hover]:bg-white/10">
                        <MessageTools text={m.content} testId={`ai-msg-${i}`} disabled={!m.id || sendingMsg}
                          onEdit={() => startEdit(m, i)} onResend={() => send(m.content, m.mode, { id: m.id, index: i }, (m.attachments_meta || []).filter((a) => a.id))} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {streamingText && (
                <div className="flex justify-start" data-testid="ai-streaming">
                  <div className="max-w-[88%] rounded-xl px-4 py-3 text-sm bg-[#FDF9F1] text-slate-900 border border-[#C5A059]/40 shadow-sm">
                    <AiMarkdown>{stripRetenir(streamingText).texte}</AiMarkdown>
                    <span className="inline-block w-1.5 h-4 ml-0.5 bg-[#C5A059] animate-pulse align-middle rounded-sm"></span>
                  </div>
                </div>
              )}
              {sendingMsg && !streamingText && (
                <div className="flex justify-start">
                  <div className="bg-[#FDF9F1] border border-[#C5A059]/20 px-4 py-3 rounded-xl flex items-center gap-2 text-xs text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin text-[#C5A059]" /> Préparation…
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {attachments.length > 0 && (
          <div className="px-6 py-2 border-t border-slate-100">
            <PiecesChips pieces={attachments} testId="ai-compose" onRemove={(p) => (editing ? setAttachments((x) => x.filter((y) => y.id !== p.id)) : retirerPiece(p, setAttachments))} />
          </div>
        )}

        {editing && <EditBanner testId="ai" onCancel={cancelEdit} />}
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="p-4 border-t border-slate-200 flex gap-2 items-end">
          <AttachButton contexte="conseil" sessionId={activeId} pieces={attachments} setPieces={setAttachments} disabled={sendingMsg} testId="ai" />
          <VoiceRecorder onTranscribed={(t) => setInput((v) => (v ? v + " " : "") + t)} />
          <Textarea
            ref={inputRef}
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={mode === "draft_convention" ? "Décrivez la convention à rédiger…" : mode === "draft_courrier" ? "Décrivez le courrier…" : mode === "search_ged" ? "Que cherchez-vous dans la GED ?" : "Posez votre question…"}
            data-testid="ai-input"
            className="resize-none"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
          />
          {sendingMsg ? (
            <Button type="button" onClick={cancelStream} data-testid="ai-stop" className="bg-red-500 hover:bg-red-600 text-white self-stretch">
              <StopCircle className="h-4 w-4" />
            </Button>
          ) : (
            <Button type="submit" disabled={!input.trim()} data-testid="ai-send" className="bg-[#C5A059] hover:bg-[#A88846] text-white self-stretch">
              {editing ? <span className="text-xs font-semibold">Renvoyer</span> : <Send className="h-4 w-4" />}
            </Button>
          )}
        </form>
      </section>

      {/* Share modal */}
      {sharingId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setSharingId(null)}>
          <div className="bg-white rounded-xl p-6 w-96" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-900 mb-3">Partager avec les rôles</h3>
            <p className="text-xs text-slate-500 mb-3">Les utilisateurs ayant un des rôles cochés pourront consulter cette conversation.</p>
            <div className="space-y-2">
              {SHAREABLE_ROLES.map((r) => (
                <label key={r.value} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={shareRoles.includes(r.value)} onChange={(e) => setShareRoles(e.target.checked ? [...shareRoles, r.value] : shareRoles.filter((x) => x !== r.value))} />
                  {r.label}
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setSharingId(null)}>Annuler</Button>
              <Button onClick={saveShare} className="bg-[#C5A059] hover:bg-[#A88846] text-white" data-testid="ai-share-save">Enregistrer</Button>
            </div>
          </div>
        </div>
      )}

      {/* Templates modal */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowTemplates(false)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="ai-templates-modal">
            <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-[#FDF9F1] to-white">
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-[#C5A059]" />
                <div>
                  <h3 className="font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Bibliothèque de modèles</h3>
                  <p className="text-xs text-slate-500">{templates.length} modèles prêts à l'emploi — remplissez les crochets [XXX] puis envoyez</p>
                </div>
              </div>
              <button onClick={() => setShowTemplates(false)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </header>
            <div className="px-6 py-3 border-b border-slate-100 flex flex-wrap gap-2">
              <button onClick={() => setTplCategory("all")} className={`text-xs px-3 py-1 rounded-full border ${tplCategory === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`} data-testid="ai-tpl-cat-all">Tous ({templates.length})</button>
              {Object.entries(grouped).map(([cat, items]) => {
                const meta = CATEGORY_META[cat] || CATEGORY_META.autre;
                const Icon = meta.icon;
                const active = tplCategory === cat;
                return (
                  <button key={cat} onClick={() => setTplCategory(cat)} data-testid={`ai-tpl-cat-${cat}`} className={`text-xs px-3 py-1 rounded-full border flex items-center gap-1 transition-colors ${active ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:border-[#C5A059]"}`} style={active ? { backgroundColor: meta.color } : {}}>
                    <Icon className="h-3 w-3" /> {meta.label} ({items.length})
                  </button>
                );
              })}
            </div>
            <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredTemplates.length === 0 && (
                <div className="col-span-full text-center text-sm text-slate-500 py-10">Aucun modèle dans cette catégorie.</div>
              )}
              {filteredTemplates.map((tpl) => {
                const meta = CATEGORY_META[tpl.category] || CATEGORY_META.autre;
                const Icon = meta.icon;
                return (
                  <button key={tpl.id} onClick={() => applyTemplate(tpl)} data-testid={`ai-tpl-${tpl.id}`} className="text-left bg-white hover:bg-[#FDF9F1] border border-slate-200 hover:border-[#C5A059] rounded-lg p-4 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: `${meta.color}20`, color: meta.color }}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-sm text-slate-900 truncate">{tpl.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{tpl.description || tpl.prompt_template.slice(0, 100) + "…"}</div>
                        <div className="text-[10px] text-[#A88846] mt-1 uppercase tracking-wide">{meta.label}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function EmptyState({ templates, onPick }) {
  const quick = templates.slice(0, 4);
  return (
    <div className="text-center py-6">
      <div className="h-14 w-14 mx-auto rounded-full bg-[#FDF9F1] border border-[#C5A059]/30 flex items-center justify-center mb-4">
        <Sparkles className="h-6 w-6 text-[#C5A059]" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Comment puis-je vous aider ?</h3>
      <p className="text-sm text-slate-500 mt-1">Chat, documents, brouillons, recherche GED et dictée vocale.</p>
      {quick.length > 0 && (
        <>
          <p className="text-xs uppercase tracking-wide text-slate-400 mt-6 mb-2">Modèles populaires</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
            {quick.map((tpl, i) => {
              const meta = CATEGORY_META[tpl.category] || CATEGORY_META.autre;
              const Icon = meta.icon;
              return (
                <button key={tpl.id} onClick={() => onPick(tpl)} data-testid={`ai-suggest-${i}`} className="text-left text-sm bg-slate-50 hover:bg-[#FDF9F1] hover:border-[#C5A059] border border-slate-200 px-4 py-3 rounded-md transition-colors flex items-start gap-2">
                  <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: meta.color }} />
                  <span className="truncate">{tpl.title}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
